import { Injectable, Inject } from '@angular/core';
import { IMessage } from '@stomp/stompjs';
import { StompHeaders, RxStompService, StompConfig } from '@stomp/ng2-stompjs';
import { cloneDeep } from 'lodash';
import { Subscription, Observable, BehaviorSubject } from 'rxjs';
import { map, first, tap } from 'rxjs/operators';
import {
  BenchmarkService,
  TimeRangeType,
  TimeoutWrapper,
  UserService,
  NotificationService,
  AutoRefreshService
} from '@nrwl/common-lib';
import { prepareFakeResponse } from '../data/query.builder';
import { Query } from '../data/query';
import { QueryEmitter } from './query.emiter';
import { IEvent, IWidgetQueriesRegister, IWidgetQuery, IQueryRequest } from './types';

export class SubscriptionRules {
  constructor(
    private readonly channel: string,
    private readonly unsubscribeChannel: string,
    private readonly mandatoryFillIn: string[] = [],
    private readonly mandatoryEmpty: string[] = []
  ) {}

  getChannel(): string {
    return this.channel;
  }

  getUnsubscribeChannel(): string {
    return this.unsubscribeChannel;
  }

  isValid(query: IQueryRequest): string {
    let wrongFields = '';
    this.mandatoryFillIn.forEach(fieldName => {
      if (!query[fieldName]) {
        wrongFields += `+${fieldName},`;
      }
    });
    this.mandatoryEmpty.forEach(fieldName => {
      if (query[fieldName]) {
        wrongFields += `-${fieldName},`;
      }
    });
    return wrongFields;
  }
}

const CancelURL = '/exchange/DataEmitter_cancel_query_exchange';
export const SubscribeURL = {
  DELTA: new SubscriptionRules(
    '/exchange/DataEmitter_delta_queries_subscribe',
    '/exchange/DataEmitter_delta_queries_unsubscribe',
    ['fromTime', 'timezone'],
    ['toTime', 'timerange']
  ),
  FIXED: new SubscriptionRules(
    '/exchange/DataEmitter_fixed_timerange_subscribe',
    '/exchange/DataEmitter_fixed_timerange_unsubscribe',
    ['timerange', 'timezone'],
    ['toTime', 'fromTime']
  ),
  HISTORICAL: new SubscriptionRules(
    '/exchange/DataEmitter_historic_query_requests',
    undefined, // no need to unsubscribe/one time query
    ['fromTime', 'toTime', 'timezone'],
    ['timerange']
  )
};

/**
 */
@Injectable()
export class QueryEmitterProvider extends QueryEmitter {
  // widget sessionID to widget-related query space
  widgets: Map<string, IWidgetQueriesRegister> = new Map();
  timeout: TimeoutWrapper;

  constructor(
    private readonly _stompService: RxStompService,
    private readonly userService: UserService,
    @Inject('defaultSocketsConfig') readonly stompConfig: StompConfig,
    private readonly benchmarkService: BenchmarkService,
    private readonly notificationService: NotificationService,
    private readonly autoRefreshService: AutoRefreshService
  ) {
    super();
    this.timeout = new TimeoutWrapper();
    if (this.userService.profileEvents) {
      this.userService.profileEvents.subscribe(event => {
        this.reconfigureStomp(event);
      });
    }
  }

  reconfigureStomp(event: IEvent): void {
    if (event?.event === 'logout') {
      if (this._stompService.active) {
        this._stompService.deactivate();
      }
    } else {
      // the 'profileEvents' subject on userService is holding up only the last event.
      // in certain scenarios, query.emitter.provider will subscribe to the Subject AFTER
      // 'login' event was superseded with 'user-prefs' event.
      // Or, if user presses F5, there will be no 'login' event at all (user login shall be reused)
      // so we do not try to recognize all those events - and simply check whether stomp should be
      // activated.
      if (!this._stompService.active) {
        this.activateStompService();
      }
    }
  }

  activateStompService(): void {
    const userName = this.userService.getUserName();
    const jwtToken = this.userService.getUserAuth().getToken();

    const configTemplate = cloneDeep(this.stompConfig);
    configTemplate.connectHeaders.login = userName;
    configTemplate.connectHeaders.passcode = jwtToken;

    this._stompService.configure(configTemplate);
    this._stompService.activate();
  }

  /**
   * Each widget instance must set-up a 'session'. Session object is used by this service
   * to track all queries for that widget.
   */
  generateWidgetSessionId(): string {
    return this.generateUUID();
  }

  generateExtendedUUID(viewId: string, instanceId: string): string {
    return `${instanceId}_${this.generateUUID()}`;
  }

  generateUUID(): string {
    // TODO: rely on a lib like this one: https://www.npmjs.com/package/uuid
    // to generate UUIDs.
    // for now use random.
    return Math.random().toString(36).substr(2);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  submitPeriodic(
    widgetSessionId: string,
    key: string,
    query: Query,
    callback: Function //eslint-disable-line
  ): Subscription {
    return this.submitQuery(widgetSessionId, key, query, SubscribeURL.DELTA, callback, false);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  submitOneTime(
    widgetSessionId: string,
    key: string,
    query: Query,
    callback: Function //eslint-disable-line
  ): Subscription {
    return this.submitQuery(widgetSessionId, key, query, SubscribeURL.DELTA, callback, true);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  submitPMW(widgetSessionId: string, key: string, query: Query, callback: Function): Subscription {
    return this.submitQuery(widgetSessionId, key, query, SubscribeURL.FIXED, callback, false);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  submitPMS(widgetSessionId: string, key: string, query: Query, callback: Function): Subscription {
    return this.submitQuery(widgetSessionId, key, query, SubscribeURL.DELTA, callback, false);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  submitOTFR(widgetSessionId: string, key: string, query: Query, callback: Function): Subscription {
    return this.submitQuery(widgetSessionId, key, query, SubscribeURL.HISTORICAL, callback, true);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  submitQuery(
    widgetSessionId: string,
    key: string,
    query: Query,
    subscribeUrl: SubscriptionRules,
    // eslint-disable-next-line @typescript-eslint/ban-types
    callback: Function,
    oneTime: boolean
  ): Subscription {
    // reset auto refresh when new query calls from component
    if (!oneTime) {
      this.autoRefreshService.resetAutoRefresh();
    }

    let register: IWidgetQueriesRegister = this.widgets.get(widgetSessionId);
    if (!register) {
      register = this.registerWidget(widgetSessionId);
    }
    const previousQuery: IWidgetQuery = register.queries.get(key);
    if (previousQuery) {
      this.cancelQuery(previousQuery, widgetSessionId, key);
    }
    const nextQuery: IWidgetQuery = this.registerQuery(query.uuid, register.askMocked);
    nextQuery.remoteChannel = subscribeUrl;
    nextQuery.subscription = this.registerCallbackOnQuery(
      widgetSessionId,
      key,
      nextQuery,
      callback,
      oneTime
    );
    register.queries.set(key, nextQuery);
    this.sendQuery(subscribeUrl, nextQuery, query, callback);
    // expose subscription/giving clients a chance to cancel themselves
    return nextQuery.subscription;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  chooseQuery(
    type: TimeRangeType,
    widgetSessionId: string,
    key: string,
    q: Query,
    callback: Function //eslint-disable-line
  ): Subscription {
    switch (type) {
      case TimeRangeType.MOVING_WINDOW:
        return this.submitPMW(widgetSessionId, key, q, callback);
      case TimeRangeType.MONITORING_SESSION:
        return this.submitPMS(widgetSessionId, key, q, callback);
      case TimeRangeType.FIXED_RANGE:
        return this.submitOTFR(widgetSessionId, key, q, callback);
      default:
        throw new Error(`TimeRangeType ${type} not implemented.`);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  registerCallbackOnQuery(
    widgetSessionId: string,
    key: string,
    query: IWidgetQuery,
    listener: any, //eslint-disable-line
    oneTime = false
  ): Subscription {
    return query.dataSource
      .pipe(
        oneTime
          ? first(() => {
              this.cancelQuery(query, widgetSessionId, key);
              return true;
            })
          : tap(),
        map((msg: IMessage) => {
          if (msg && msg.body) {
            let bodyStr = msg.body;
            try {
              bodyStr = JSON.parse(bodyStr);
              // some widgets are still bound to mocks. And mocks ask for double unencode
              try {
                bodyStr = JSON.parse(bodyStr);
                return bodyStr;
              } catch (e1) {
                if (bodyStr['status'] === 'error') {
                  this.showFailedDBNotification(bodyStr['errorMessage']);
                  console.warn('Query failed: ', bodyStr);
                }
                return bodyStr;
              }
            } catch (e) {
              return [];
            }
          }
          return msg;
        })
      )
      .subscribe(listener);
  }

  showFailedDBNotification(erroMsg: string): void {
    if (erroMsg.includes('CannotGetJdbcConnectionException')) {
      const options = { timeOut: 0 };
      this.notificationService.onFail(
        `Application database is currently offline. Contact system administration`,
        options
      );
    }
  }

  subscribeOnStomp(clientId: string): Observable<IMessage> {
    const headers: StompHeaders = {};
    headers['auto-delete'] = 'true';
    // Stream of messages
    return this._stompService.watch('/queue/DataEmitter_DataUpdate_' + clientId, headers);
  }

  // tells the backend we dont need a channel anymore
  unsubscribeOnBackend(clientId: string, unsubscribeChannel: string): void {
    this._stompService.publish({
      destination: unsubscribeChannel,
      body: JSON.stringify({ uid: clientId })
    });
  }

  // uuid is part of stringified q object
  // eslint-disable-next-line @typescript-eslint/ban-types
  sendQuery(
    subscribeUrl: SubscriptionRules,
    register: IWidgetQuery,
    q: Query,
    callback: Function //eslint-disable-line
  ): void {
    const errorsList = subscribeUrl.isValid(q.data);
    if (errorsList.length === 0) {
      this.benchmarkService.registerRequestEvent({ uid: q.uuid, query: q.data, url: subscribeUrl });
      this._stompService.publish({
        destination: subscribeUrl.getChannel(),
        body: JSON.stringify(q.data)
      });
    } else {
      console.error(
        'Query not valid (+:field must be present, -:field must be absent)',
        errorsList,
        q
      );
      prepareFakeResponse(this.timeout, q, callback, true);
    }
  }

  registerQuery(uuid: string, askMocked = false): IWidgetQuery {
    const nextQuery: IWidgetQuery = {
      uuid,
      dataSource: null,
      subscription: null,
      remoteChannel: null
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let s: Observable<any> = new BehaviorSubject([]);
    if (!askMocked) {
      s = this.subscribeOnStomp(nextQuery.uuid);
    }
    nextQuery.dataSource = s;
    return nextQuery;
  }

  registerWidget(widgetSessionId: string, askMocked = false): IWidgetQueriesRegister {
    const register = {
      queries: new Map(),
      askMocked
    };
    this.widgets.set(widgetSessionId, register);
    return register;
  }

  unregisterWidget(widgetSessionId: string): void {
    const register: IWidgetQueriesRegister = this.widgets.get(widgetSessionId);
    if (!register) {
      return;
    }
    register.queries.forEach((currentQuery: IWidgetQuery, key: string) => {
      this.cancelQuery(currentQuery, widgetSessionId, key);
    });
    this.widgets.delete(widgetSessionId);
  }

  cancelQuery(query: IWidgetQuery, widgetSessionId: string, key: string): void {
    if (query.subscription) {
      this.timeout.add(() => query.subscription.unsubscribe(), 0);
      if (query.remoteChannel.getUnsubscribeChannel()) {
        this.unsubscribeOnBackend(query.uuid, query.remoteChannel.getUnsubscribeChannel());
      }
    }

    if (widgetSessionId && key) {
      const register: IWidgetQueriesRegister = this.widgets.get(widgetSessionId);
      if (register && register.queries) {
        register.queries.delete(key);
      }
    }
  }

  onDestroy(widgetSessionId?: string): void {
    if (widgetSessionId) {
      this.unregisterWidget(widgetSessionId);
    } else {
      this.widgets.forEach((value: IWidgetQueriesRegister, key: string) => {
        this.unregisterWidget(key);
      });
    }
  }

  // uuid is part of stringified q object
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  sendCancelQuery(data): void {
    this._stompService.publish({
      destination: CancelURL,
      body: JSON.stringify(data)
    });
  }
}
